import React, { type ReactNode, useCallback, useRef, useState } from 'react';
import {
  RadioGroup as RadioGroupRAC,
  Radio as RadioRAC,
} from 'react-aria-components';

import { useFieldAttributes } from '../../contexts/field-attributes-context';
import { useFieldPropsV2 } from '../../contexts/field-props-context';
import { findChildOfType, applyInternalProps } from '../../services/find-child';
import { Description } from '../field-description/field-description-headless';
import { Error } from '../field-error/field-error-headless';
import { Label } from '../field-label/field-label-headless';

import { type RatingInputFieldProps } from './rating-input-field-props';

export type RatingInputProps = {
  children: ReactNode;
};

export const RatingInput = ({ children }: RatingInputProps) => {
  const {
    id,
    value,
    onChange,
    disabled,
    required,
    hasError,
    onBlur,
    onFocus,
    FieldLayout,
  } = useFieldPropsV2<RatingInputFieldProps>();

  const handleChange = useCallback(
    (selectedValue: string) =>
      onChange(Number(selectedValue) as RatingInputFieldProps['value']),
    [onChange],
  );

  const labelElement = findChildOfType(children, RatingInput.Label);
  const starsElement = findChildOfType(children, RatingInput.Stars);
  const descriptionElement = findChildOfType(children, RatingInput.Description);
  const errorElement = findChildOfType(children, RatingInput.Error);

  return (
    <FieldLayout
      fieldId={id}
      renderLabel={() => labelElement}
      renderInput={() => (
        <RadioGroupRAC
          value={value ? String(value) : undefined}
          onChange={handleChange}
          isDisabled={disabled}
          isRequired={required}
          isInvalid={hasError}
          onBlur={onBlur}
          onFocus={onFocus}
          orientation="horizontal"
        >
          {starsElement}
        </RadioGroupRAC>
      )}
      renderDescription={() => (
        <>
          {descriptionElement}
          {errorElement}
        </>
      )}
    />
  );
};

type StarsProps = {
  children: ReactNode;
  className?: string;
  count?: number;
};

const Stars = ({ children, className }: StarsProps) => {
  const { value, hasError } = useFieldPropsV2<RatingInputFieldProps>();
  const { inputId, ariaDescribedBy } = useFieldAttributes();
  const [hovered, setHovered] = useState<string>();
  const containerRef = useRef<HTMLDivElement>(null);

  const starElement = findChildOfType(children, Stars.Star);
  const starValues = ['1', '2', '3', '4', '5'];
  const isFilled = (starValue: string) =>
    hovered ? hovered >= starValue : !!value && value >= Number(starValue);

  return (
    <div
      ref={containerRef}
      id={inputId}
      aria-describedby={ariaDescribedBy}
      className={className}
      data-invalid={hasError}
    >
      {starValues.map((starValue) =>
        applyInternalProps<StarInternalProps>(starElement, {
          key: starValue,
          value: starValue,
          isFilled: isFilled(starValue),
          onHoverChange: (isHover: boolean) =>
            setHovered(isHover ? starValue : undefined),
        }),
      )}
    </div>
  );
};

type StarProps = {
  className?: string;
};

type StarInternalProps = {
  value: string;
  isFilled: boolean;
  onHoverChange: (isHover: boolean) => void;
};

const Star = ({ className, ...internal }: StarProps) => {
  const { value, isFilled, onHoverChange } = internal as StarInternalProps;

  return (
    <RadioRAC
      value={value}
      className={className}
      data-filled={isFilled || undefined}
      onHoverChange={onHoverChange}
    />
  );
};

Stars.Star = Star;

RatingInput.Label = Label;
RatingInput.Stars = Stars;
RatingInput.Star = Star;
RatingInput.Description = Description;
RatingInput.Error = Error;
